import React from 'react';
import { Link } from 'react-router-dom';

const DeleteAccountInfoPage = () => {
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4 md:p-8 safe-left safe-right safe-bottom"
      style={{ paddingTop: 'calc(var(--app-safe-area-top) + 1rem)' }}
    >
      <div className="mx-auto max-w-4xl">
        <div className="comic-card space-y-8">
          <header className="space-y-3">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              Account Deletion
            </h1>
            <p className="text-sm text-gray-600">
              Last updated: March 1, 2026
            </p>
            <p className="text-gray-700">
              This page explains how to delete your Okio account, what data is
              removed, and how to contact us if you need assistance.
            </p>
          </header>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">
              What Gets Deleted
            </h2>
            <p className="text-gray-700">
              When you delete your account, the following data is permanently
              and immediately removed from our systems:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>
                <strong>Account information:</strong> name, username, email
                address, profile photo, and all authentication credentials.
              </li>
              <li>
                <strong>Projects:</strong> all projects you created, including
                their settings, objectives, and associated world-state data.
              </li>
              <li>
                <strong>Chats and messages:</strong> every conversation and AI
                message from your projects.
              </li>
              <li>
                <strong>Uploaded files:</strong> all images and media you
                uploaded through the app.
              </li>
              <li>
                <strong>Saved lists:</strong> all custom lists and any items
                you have saved.
              </li>
              <li>
                <strong>Location and GPS data:</strong> any location or
                tracking data recorded during your sessions.
              </li>
              <li>
                <strong>Push notification tokens:</strong> device tokens used
                to send you notifications.
              </li>
              <li>
                <strong>Billing and token history:</strong> your token balance
                and purchase records.
              </li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">
              What Is Retained
            </h2>
            <p className="text-gray-700">
              We may retain aggregated, anonymised statistical data (for
              example, total number of users or sessions) that cannot be traced
              back to any individual. No personally identifiable information is
              kept after deletion.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-900">
              How to Delete Your Account In-App
            </h2>
            <p className="text-gray-700">
              You can delete your account directly from the app at any time.
              The deletion is processed immediately.
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700">
              <li>Open the Okio app and sign in to your account.</li>
              <li>
                Tap the <strong>Profile</strong> icon in the bottom navigation
                bar to go to your profile page.
              </li>
              <li>
                Tap the <strong>Delete Account</strong> button in the top-right
                action row.
              </li>
              <li>
                A confirmation dialog will appear. Read the warning carefully —
                this action cannot be undone.
              </li>
              <li>
                Type your <strong>username</strong> exactly as shown to confirm
                you understand the consequences.
              </li>
              <li>
                Tap <strong>Delete my account</strong>. Your account and all
                associated data will be permanently erased immediately.
              </li>
            </ol>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">
              How to Request Deletion by Email
            </h2>
            <p className="text-gray-700">
              If you are unable to access the app, you can request account
              deletion by sending an email to{' '}
              <a
                href="mailto:admin@okio.ai?subject=Account%20Deletion%20Request"
                className="text-purple-600 hover:text-purple-700 underline"
              >
                admin@okio.ai
              </a>{' '}
              with the subject line <strong>Account Deletion Request</strong>.
              Please include the email address or username associated with your
              account so we can locate it. Email requests are processed within
              30 days.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">
              Processing Time
            </h2>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>
                <strong>In-app deletion:</strong> immediate — your account and
                data are erased as soon as you confirm.
              </li>
              <li>
                <strong>Email requests:</strong> processed within 30 days of
                receipt.
              </li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">Contact Us</h2>
            <p className="text-gray-700">
              If you have questions about account deletion or your data, please
              reach out to us at{' '}
              <a
                href="mailto:admin@okio.ai"
                className="text-purple-600 hover:text-purple-700 underline"
              >
                admin@okio.ai
              </a>
              .
            </p>
          </section>

          <div className="pt-2 border-t border-gray-200">
            <Link to="/" className="text-purple-600 hover:text-purple-700 text-sm font-medium">
              ← Back to app
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountInfoPage;
